import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { User, Calendar, Award, ArrowLeft } from "lucide-react";
import apiClient from "../api/apiClient";
import { Helmet } from "react-helmet-async";

const CertificateDetail = () => {
  const { id } = useParams();
  const [certificate, setCertificate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCertificate = async () => {
      try {
        const response = await apiClient.get(`/certifications/${id}`);
        setCertificate(response.data);
      } catch (err) {
        setError("Certificat non trouvé");
      } finally {
        setLoading(false);
      }
    };
    fetchCertificate();
  }, [id]);

  if (loading) {
    return (
      <div className="container py-5">
        <div className="d-flex justify-content-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
        <Link to="/" className="btn btn-primary">
          <ArrowLeft size={16} className="me-2" /> Retour à l'accueil
        </Link>
      </div>
    );
  }

  if (!certificate) {
    return (
      <div className="container py-5">
        <div className="alert alert-warning" role="alert">
          Certificat non trouvé
        </div>
        <Link to="/" className="btn btn-primary">
          <ArrowLeft size={16} className="me-2" /> Retour à l'accueil
        </Link>
      </div>
    );
  }

  {
    certificate && (
      <Helmet>
        <title>{certificate.nom_certification} – DVELGE Formation</title>
        <meta
          name="description"
          content={
            certificate.description_certification?.slice(0, 160) ||
            "Certification DVELGE Formation"
          }
        />
        <meta
          name="keywords"
          content={`certificat ${certificate.nom_certification}, DVELGE Formation, certification informatique Guinée`}
        />
        {/* Open Graph */}
        <meta
          property="og:title"
          content={`${certificate.nom_certification} – DVELGE Formation`}
        />
        <meta
          property="og:description"
          content={certificate.description_certification?.slice(0, 160)}
        />
        <meta
          property="og:image"
          content={certificate.image_path || "/assets/img/preview.png"}
        />
        <meta
          property="og:url"
          content={`https://formation.dvelgegroup.com/certificate-detail/${certificate.id_certification}`}
        />
        <meta property="og:type" content="website" />
        {/* Twitter */}
        <meta
          name="twitter:title"
          content={`${certificate.nom_certification} – DVELGE Formation`}
        />
        <meta
          name="twitter:description"
          content={certificate.description_certification?.slice(0, 160)}
        />
        <meta
          name="twitter:image"
          content={certificate.image_path || "/assets/img/preview.png"}
        />
        {/* JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CreativeWork",
            name: certificate.nom_certification,
            description: certificate.description_certification,
            author: certificate.user_name,
            datePublished: certificate.date_obtention,
            image: certificate.image_path,
            url: `https://formation.dvelgegroup.com/certificate-detail/${certificate.id_certification}`,
          })}
        </script>
      </Helmet>
    );
  }

  return (
    <section className="certificate-detail py-5">
      <div className="container">
        <div className="row">
          <div className="col-12 mb-4">
            <Link to="/" className="btn btn-outline-primary mb-4">
              <ArrowLeft size={16} className="me-2" /> Retour
            </Link>
            <div className="card border-0 shadow-sm">
              <div className="row g-0">
                <div className="col-md-6">
                  <div className="certificate-image h-100">
                    <img
                      src={certificate.image_path}
                      alt={certificate.nom_certification || "Certificat"}
                      className="img-fluid h-100 object-cover"
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="card-body p-4 p-lg-5">
                    <h1 className="card-title mb-4">
                      {certificate.nom_certification || "Certificat"}
                    </h1>
                    {certificate.description_certification && (
                      <p className="card-text mb-4">
                        {certificate.description_certification}
                      </p>
                    )}
                    <div className="certificate-meta mb-4">
                      {certificate.user_name && (
                        <div className="d-flex align-items-center mb-3">
                          <User size={20} className="text-primary me-2" />
                          <span className="fw-medium">Apprenant:</span>
                          <span className="ms-2">{certificate.user_name}</span>
                        </div>
                      )}
                      {certificate.date_obtention && (
                        <div className="d-flex align-items-center mb-3">
                          <Calendar size={20} className="text-primary me-2" />
                          <span className="fw-medium">Date d'obtention:</span>
                          <span className="ms-2">
                            {new Date(
                              certificate.date_obtention,
                            ).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                      {certificate.duree_validite && (
                        <div className="d-flex align-items-center mb-3">
                          <Award size={20} className="text-primary me-2" />
                          <span className="fw-medium">Durée de validité:</span>
                          <span className="ms-2">
                            {certificate.duree_validite}{" "}
                            {certificate.duree_validite === 1 ? "an" : "ans"}
                          </span>
                        </div>
                      )}
                    </div>
                    {certificate.qr_code && (
                      <div className="qr-code-container mb-4">
                        <h5 className="mb-3">QR Code de vérification</h5>
                        <img
                          src={certificate.qr_code}
                          alt="QR Code"
                          className="img-fluid"
                          style={{ maxWidth: "150px" }}
                        />
                      </div>
                    )}
                    {certificate.id_user && (
                      <Link
                        to={`/profil/${certificate.id_user}`}
                        className="btn btn-primary"
                      >
                        Voir le profil de l'apprenant
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CertificateDetail;
